/**
 * KycClient - TypeScript SDK for Vesant KYC (Know Your Customer) Service
 *
 * Provides type-safe methods to interact with the KYC service API.
 * Extends BaseClient for consistent retry logic, timeout handling, and error management.
 */

import { BaseClient } from '../core/client';
import type { BaseClientConfig } from '../core/config';
import type {
  KycClientConfig,
  KycRequest,
  KycRequestFilters,
  KycRequestListResponse,
  DocumentVerificationRequest,
  DocumentVerificationResponse,
  UpdateKycStatusRequest,
  RequestAdditionalDocumentsRequest,
  RequestKycSubmitLinkRequest,
  RequestKycSubmitLinkResponse,
  CheckKycStatusRequest,
  CheckKycStatusResponse,
  ProofDownloadURL,
  KycOverview,
  KycAlert,
  KycAlertFilters,
  KycAlertListResponse,
  UpdateKycAlertRequest,
  KycPreferences,
  UpdateKycPreferencesRequest,
  PaginationParams,
  CustomerProfile,
  CustomerProfileFilters,
  CustomerProfileListResponse,
  CreateCustomerProfileRequest,
  CreateReuseKycSessionRequest,
  CreateReuseKycSessionResponse,
  SubmitReuseKycSessionRequest,
} from './types';

/** Helper to add filter value to params, joining arrays with commas */
function addFilterParam(
  params: Record<string, unknown>,
  key: string,
  value: string | string[] | undefined
): void {
  if (value !== undefined) {
    params[key] = Array.isArray(value) ? value.join(',') : value;
  }
}

/** Helper to add simple filter values to params */
function addSimpleParams(
  params: Record<string, unknown>,
  source: Record<string, unknown>,
  keys: string[]
): void {
  for (const key of keys) {
    if (source[key] !== undefined) {
      params[key] = source[key];
    }
  }
}

/**
 * KycClient extends BaseClient for:
 * - Consistent retry logic with exponential backoff
 * - Unified error handling (VesantError, NetworkError, TimeoutError, etc.)
 * - Automatic timeout management
 * - Debug logging
 *
 * All KYC verification calls use requestWithRetry() to handle
 * transient failures gracefully.
 */
export class KycClient extends BaseClient {
  private userId?: string;
  private riskProfileBaseURL?: string;

  constructor(config: KycClientConfig) {
    // Convert KycClientConfig to BaseClientConfig
    const baseConfig: BaseClientConfig = {
      baseURL: config.baseURL,
      tenantId: config.tenantId,
      apiKey: config.apiKey,
      headers: config.headers,
      timeout: config.timeout,
      retries: 3, // Default retry count for KYC calls
      debug: config.debug,
      environment: config.environment,
    };
    super(baseConfig);
    this.userId = config.userId;
    this.riskProfileBaseURL = config.riskProfileBaseURL;
  }

  /**
   * Set the user ID for operations requiring user context
   */
  setUserId(userId: string): void {
    this.userId = userId;
  }

  /**
   * Get current user ID
   */
  getUserId(): string | undefined {
    return this.userId;
  }

  /**
   * Build headers including user ID if available
   */
  private getUserHeaders(): Record<string, string> {
    const headers: Record<string, string> = {};
    if (this.userId) {
      headers['X-User-ID'] = this.userId;
    }
    return headers;
  }

  // ============================================================================
  // Document Verification
  // ============================================================================

  /**
   * Request a KYC submission redirect URL for a user
   *
   * Generates a link that the user can visit to submit their KYC documents.
   *
   * @param request - Request containing the user ID, redirect URL, callback URL, and trigger event
   * @returns Response containing kyc_required, can_skip, and optionally the redirect link and KYC ID
   *
   * @example
   * ```typescript
   * const result = await client.requestKycSubmitLink({
   *   user_id: "user_123",
   *   redirect_url: "https://merchant.com/kyc-complete",
   *   callback_url: "https://merchant.com/api/kyc-webhook",
   *   trigger_event: "onboarding"
   * });
   *
   * if (result.kyc_required && result.link) {
   *   console.log(`Redirect user to: ${result.link}`);
   * } else if (result.can_skip) {
   *   console.log("KYC not required, user can proceed");
   * }
   * ```
   */
  async requestKycSubmitLink(
    request: RequestKycSubmitLinkRequest
  ): Promise<RequestKycSubmitLinkResponse> {
    return this.requestWithRetry<RequestKycSubmitLinkResponse>('/api/v1/kyc/request', {
      method: 'POST',
      body: JSON.stringify(request),
      headers: this.getUserHeaders(),
    });
  }

  /**
   * Create a reuse KYC session for validate a user with existing KYC verification
   *
   * @param request - Request containing the reference, customer_id, optional redirect URL, and callback URL (receives POST requests)
   */
  async createReuseKycSession(request: CreateReuseKycSessionRequest): Promise<CreateReuseKycSessionResponse> {
    return this.requestWithRetry<CreateReuseKycSessionResponse>('/api/v1/kyc/face/session', {
      method: 'POST',
      body: JSON.stringify(request),
      headers: this.getUserHeaders(),
    });
  }

  /**
   * Submit a reuse KYC session for validate a user with existing KYC verification
   *
   * @param request - Request containing the reference, token and proof (receives POST requests)
   */
  async submitReuseKycSession(request: SubmitReuseKycSessionRequest): Promise<CheckKycStatusResponse> {
    return this.requestWithRetry<CheckKycStatusResponse>('/api/v1/kyc/face/submit', {
      method: 'POST',
      body: JSON.stringify(request),
      headers: this.getUserHeaders(),
    });
  }

  /**
   * Check reuse KYC session status for a reference
   * @param reference - The unique reference used for the reuse KYC session (e.g., customer ID or transaction ID)
   * @returns Response with kyc_status and message (reason)
   * **/
  async getReuseKycSessionStatus(reference: string): Promise<CheckKycStatusResponse> {
    return this.requestWithRetry<CheckKycStatusResponse>(`/api/v1/kyc/face/verify/${encodeURIComponent(reference)}`, {
      method: 'GET',
      headers: this.getUserHeaders(),
    });
  }
  /**
   * Check KYC status for a user
   *
   * Returns the current KYC status:
   * - "complete" → KYC verified, show "Continue to Login"
   * - "processing" → Under review, show "Continue to Login"
   * - "failed" / "declined" → Show error + retry option
   * - "pending" / "in_progress" → Keep polling
   *
   * @param request - Request containing user_id and token
   * @returns Response with kyc_status and message (reason)
   *
   * @example
   * ```typescript
   * const result = await client.checkKycStatus({
   *   user_id: "user_123",
   *   token: "auth_token_xyz"
   * });
   *
   * if (result.kyc_status === 'complete') {
   *   // Show "Continue to Login"
   * } else if (result.kyc_status === 'failed' || result.kyc_status === 'declined') {
   *   // Show error + retry option
   * } else {
   *   // Keep polling
   * }
   * ```
   */
  async checkKycStatus(
    request: CheckKycStatusRequest
  ): Promise<CheckKycStatusResponse> {
    const params = {
      user_id: request.user_id,
      kyc_id: request.kyc_id,
    };

    return this.requestWithRetry<CheckKycStatusResponse>(
      `/api/v1/kyc/request/status${this.buildQueryString(params)}`,
      {
        method: 'GET',
        headers: this.getUserHeaders(),
      }
    );
  }

  /**
   * Submit a document verification request with OCR
   *
   * Uses requestWithRetry() for automatic retry on transient failures.
   *
   * @param request - Document verification request with documents and customer info
   * @returns Verification response with status and reference
   *
   * @example
   * ```typescript
   * const result = await client.submitVerification({
   *   reference: "customer_123",
   *   email: "customer@example.com",
   *   country: "US",
   *   document: {
   *     proof: base64EncodedImage,
   *     selected_type: ["id_card", "passport"],
   *     name: { first_name: "John", last_name: "Doe" },
   *     dob: "1990-01-15"
   *   },
   *   face: {
   *     proof: base64EncodedSelfie
   *   }
   * });
   *
   * console.log(`Verification submitted: ${result.reference}`);
   * ```
   */
  async submitVerification(
    request: DocumentVerificationRequest
  ): Promise<DocumentVerificationResponse> {
    return this.requestWithRetry<DocumentVerificationResponse>('/api/v1/kyc/submit', {
      method: 'POST',
      body: JSON.stringify(request),
      headers: this.getUserHeaders(),
    });
  }

  /**
   * Get a KYC request by ID
   *
   * @param id - KYC request ID
   * @returns KYC request details including proofs and alerts
   *
   * @example
   * ```typescript
   * const kyc = await client.getKycRequest("kyc_abc123");
   * console.log(`Status: ${kyc.status}, ID Verified: ${kyc.id_verified}`);
   * ```
   */
  async getKycRequest(id: string): Promise<KycRequest> {
    return this.request<KycRequest>(`/api/v1/kyc/requests/${id}`);
  }

  /**
   * List KYC requests with filters and pagination
   *
   * @param filters - Optional filters (status, search, date range)
   * @param pagination - Optional pagination (page, page_size)
   * @returns Paginated list of KYC requests
   *
   * @example
   * ```typescript
   * const requests = await client.listKycRequests(
   *   { status: "pending", search: "john" },
   *   { page: 1, page_size: 20 }
   * );
   * console.log(`Found ${requests.total} pending requests`);
   * ```
   */
  async listKycRequests(
    filters?: KycRequestFilters,
    pagination?: PaginationParams
  ): Promise<KycRequestListResponse> {
    const params: Record<string, unknown> = {};

    if (filters) {
      addFilterParam(params, 'status', filters.status);
      addSimpleParams(params, filters as unknown as Record<string, unknown>, ['search', 'fromDate', 'toDate', 'sortBy', 'order']);
    }

    if (pagination) {
      if (pagination.page) params.page = pagination.page;
      if (pagination.page_size) params.limit = pagination.page_size;
    }

    return this.request<KycRequestListResponse>(
      `/api/v1/kyc/requests${this.buildQueryString(params)}`,
      { headers: this.getUserHeaders() }
    );
  }

  /**
   * Update KYC request status (accept/decline)
   *
   * @param request - Status update request with reference, status, and reason
   *
   * @example
   * ```typescript
   * // Accept a KYC request
   * await client.updateKycStatus({
   *   reference: "shufti_ref_123",
   *   status: "accepted",
   *   reason: "All documents verified successfully"
   * });
   *
   * // Decline a KYC request
   * await client.updateKycStatus({
   *   reference: "shufti_ref_456",
   *   status: "declined",
   *   reason: "Document expired"
   * });
   * ```
   */
  async updateKycStatus(request: UpdateKycStatusRequest): Promise<void> {
    await this.request('/api/v1/kyc/status', {
      method: 'PUT',
      body: JSON.stringify(request),
      headers: this.getUserHeaders(),
    });
  }

  /**
   * Request additional documents from customer
   *
   * @param request - Request with KYC ID and document types needed
   *
   * @example
   * ```typescript
   * await client.requestAdditionalDocuments({
   *   id: "kyc_abc123",
   *   document_types: ["address", "document_two"],
   *   message: "Please provide proof of address and secondary ID"
   * });
   * ```
   */
  async requestAdditionalDocuments(
    request: RequestAdditionalDocumentsRequest
  ): Promise<void> {
    await this.request('/api/v1/kyc/requests', {
      method: 'PUT',
      body: JSON.stringify(request),
      headers: this.getUserHeaders(),
    });
  }

  /**
   * Get downloadable URLs for KYC proofs/documents
   *
   * @param kycId - KYC request ID
   * @returns Array of proof download URLs with expiration
   *
   * @example
   * ```typescript
   * const proofs = await client.getProofDownloadURLs("kyc_abc123");
   * proofs.forEach(proof => {
   *   console.log(`${proof.type}: ${proof.url} (expires: ${proof.expires_at})`);
   * });
   * ```
   */
  async getProofDownloadURLs(kycId: string): Promise<ProofDownloadURL[]> {
    return this.request<ProofDownloadURL[]>(
      `/api/v1/kyc/proofs/${kycId}`,
      { headers: this.getUserHeaders() }
    );
  }

  // ============================================================================
  // KYC Overview & Statistics
  // ============================================================================

  /**
   * Get KYC overview statistics
   *
   * @param fromDate - Optional start date (ISO 8601)
   * @param toDate - Optional end date (ISO 8601)
   * @returns KYC statistics including counts by status
   *
   * @example
   * ```typescript
   * const overview = await client.getOverview(
   *   "2024-01-01T00:00:00Z",
   *   "2024-12-31T23:59:59Z"
   * );
   * console.log(`Total: ${overview.total}, Pending: ${overview.pending}`);
   * console.log(`Approved: ${overview.approved}, Rejected: ${overview.rejected}`);
   * ```
   */
  async getOverview(fromDate?: string, toDate?: string): Promise<KycOverview> {
    const params: Record<string, unknown> = {};
    if (fromDate) params.fromDate = fromDate;
    if (toDate) params.toDate = toDate;

    return this.request<KycOverview>(
      `/api/v1/kyc/overview${this.buildQueryString(params)}`
    );
  }

  // ============================================================================
  // Alert Management
  // ============================================================================

  /**
   * Get a KYC alert by ID
   *
   * @param alertId - Alert ID
   * @returns Alert details
   */
  async getAlert(alertId: string): Promise<KycAlert> {
    return this.request<KycAlert>(`/api/v1/kyc/alerts/${alertId}`);
  }

  /**
   * List KYC alerts with filters and pagination
   *
   * @param filters - Optional filters (status, risk, alert_type, assigned_for, etc.)
   * @param pagination - Optional pagination (page, page_size)
   * @returns Paginated list of alerts
   *
   * @example
   * ```typescript
   * const alerts = await client.listAlerts(
   *   {
   *     status: ["pending", "in_progress"],
   *     risk: "critical",
   *     alert_type: "kyc"
   *   },
   *   { page: 1, page_size: 20 }
   * );
   * console.log(`Found ${alerts.total} alerts`);
   * ```
   */
  async listAlerts(
    filters?: KycAlertFilters,
    pagination?: PaginationParams
  ): Promise<KycAlertListResponse> {
    const params: Record<string, unknown> = {};

    if (filters) {
      // Array filters that need comma joining
      addFilterParam(params, 'kyc_id', filters.kyc_id);
      addFilterParam(params, 'alert_type', filters.alert_type);
      addFilterParam(params, 'assigned_for', filters.assigned_for);
      addFilterParam(params, 'status', filters.status);
      addFilterParam(params, 'risk', filters.risk);
      // Simple filters
      addSimpleParams(params, filters as unknown as Record<string, unknown>, ['search', 'fromDate', 'toDate', 'sort_by', 'order']);
    }

    if (pagination) {
      if (pagination.page) params.page = pagination.page;
      if (pagination.page_size) params.limit = pagination.page_size;
    }

    return this.request<KycAlertListResponse>(
      `/api/v1/kyc/alerts${this.buildQueryString(params)}`
    );
  }

  /**
   * Update a KYC alert
   *
   * @param alertId - Alert ID
   * @param update - Fields to update (status, assigned_for, reason, alert_type)
   *
   * @example
   * ```typescript
   * // Assign an alert
   * await client.updateAlert("alert_123", {
   *   assigned_for: "analyst_user_id",
   *   status: "in_progress"
   * });
   *
   * // Resolve an alert
   * await client.updateAlert("alert_123", {
   *   status: "resolved",
   *   reason: "Customer verified via phone call"
   * });
   *
   * // Escalate an alert
   * await client.updateAlert("alert_123", {
   *   status: "escalated",
   *   assigned_for: "manager_user_id",
   *   reason: "Requires senior review - suspicious documents"
   * });
   * ```
   */
  async updateAlert(alertId: string, update: UpdateKycAlertRequest): Promise<void> {
    await this.request(`/api/v1/kyc/alerts/${alertId}`, {
      method: 'PUT',
      body: JSON.stringify(update),
      headers: this.getUserHeaders(),
    });
  }

  // ============================================================================
  // KYC Preferences
  // ============================================================================

  /**
   * Get KYC preferences for the tenant
   *
   * @returns KYC preferences configuration
   *
   * @example
   * ```typescript
   * const prefs = await client.getPreferences();
   * console.log(`Face verification required: ${prefs.is_face_verification_required}`);
   * console.log(`Required documents: ${prefs.required_document_count}`);
   * ```
   */
  async getPreferences(): Promise<KycPreferences> {
    return this.request<KycPreferences>('/api/v1/kyc/preferences');
  }

  /**
   * Update KYC preferences for the tenant
   *
   * @param update - Fields to update
   * @returns Updated preferences
   *
   * @example
   * ```typescript
   * await client.updatePreferences({
   *   is_face_verification_required: true,
   *   is_address_verification_required: true,
   *   required_document_count: 2,
   *   reasons: [
   *     "Documents verified",
   *     "Identity confirmed",
   *     "Address matched"
   *   ]
   * });
   * ```
   */
  async updatePreferences(
    update: UpdateKycPreferencesRequest
  ): Promise<KycPreferences> {
    return this.request<KycPreferences>('/api/v1/kyc/preferences', {
      method: 'PUT',
      body: JSON.stringify(update),
    });
  }

  // ============================================================================
  // Customer Profile Management (Risk Profile Service)
  // ============================================================================

  /**
   * Make a request to the Risk Profile Service
   * Uses riskProfileBaseURL if configured, otherwise falls back to baseURL
   */
  private async riskProfileRequest<T>(
    path: string,
    options: RequestInit = {}
  ): Promise<T> {
    if (!this.riskProfileBaseURL) {
      throw new Error(
        'Risk Profile Service URL not configured. Please provide riskProfileBaseURL in KycClientConfig.'
      );
    }

    return this.request<T>(path, {
      ...options,
      headers: {
        ...this.getUserHeaders(),
        ...(options.headers as Record<string, string>),
      },
    }, this.riskProfileBaseURL);
  }

  /**
   * List customer profiles for the tenant
   *
   * @param filters - Optional filters (risk_category, entity_type, status, search, etc.)
   * @param pagination - Optional pagination (limit, offset)
   * @returns Paginated list of customer profiles
   *
   * @example
   * ```typescript
   * const profiles = await client.listCustomerProfiles(
   *   { risk_category: 'high', entity_type: 'individual' },
   *   { limit: 20, offset: 0 }
   * );
   * console.log(`Found ${profiles.total} profiles`);
   * profiles.profiles.forEach(p => console.log(p.customer_id, p.full_name));
   * ```
   */
  async listCustomerProfiles(
    filters?: CustomerProfileFilters,
    pagination?: { limit?: number; offset?: number }
  ): Promise<CustomerProfileListResponse> {
    const params: Record<string, unknown> = {};

    if (filters) {
      // Array filters (keep as arrays for this API)
      if (filters.risk_category) {
        params.risk_category = Array.isArray(filters.risk_category)
          ? filters.risk_category
          : [filters.risk_category];
      }
      if (filters.entity_type) {
        params.entity_type = Array.isArray(filters.entity_type)
          ? filters.entity_type
          : [filters.entity_type];
      }
      if (filters.status) {
        params.status = Array.isArray(filters.status)
          ? filters.status
          : [filters.status];
      }
      // Simple filters
      addSimpleParams(params, filters as unknown as Record<string, unknown>, [
        'search', 'is_pep', 'has_sanctions', 'requires_edd', 'sort_by', 'sort_order'
      ]);
    }

    if (pagination) {
      addSimpleParams(params, pagination, ['limit', 'offset']);
    }

    return this.riskProfileRequest<CustomerProfileListResponse>(
      `/api/v1/risk-dashboard/profiles${this.buildQueryString(params)}`
    );
  }

  /**
   * Get a customer profile by ID
   *
   * @param profileId - The profile UUID
   * @returns Customer profile details
   *
   * @example
   * ```typescript
   * const profile = await client.getCustomerProfile('uuid-here');
   * console.log(`Customer: ${profile.full_name}, Risk: ${profile.risk_category}`);
   * ```
   */
  async getCustomerProfile(profileId: string): Promise<CustomerProfile> {
    return this.riskProfileRequest<CustomerProfile>(
      `/api/v1/risk-dashboard/profiles/${profileId}`
    );
  }

  /**
   * Create a new customer profile
   *
   * A customer profile must exist before submitting KYC verification.
   * The customer_id field will be used as the 'reference' in KYC submissions.
   *
   * @param profile - Customer profile data
   * @returns Created customer profile
   *
   * @example
   * ```typescript
   * const profile = await client.createCustomerProfile({
   *   customer_id: 'cust_123',
   *   entity_type: 'individual',
   *   full_name: 'John Doe',
   *   email_address: 'john@example.com',
   *   country_of_residence: 'US'
   * });
   * console.log(`Created profile: ${profile.id}`);
   *
   * // Now you can submit KYC using the customer_id as reference
   * await client.submitVerification({
   *   reference: profile.customer_id, // 'cust_123'
   *   email: 'john@example.com',
   *   // ... other fields
   * });
   * ```
   */
  async createCustomerProfile(
    profile: CreateCustomerProfileRequest
  ): Promise<CustomerProfile> {
    return this.riskProfileRequest<CustomerProfile>('/api/v1/profiles', {
      method: 'POST',
      body: JSON.stringify(profile),
    });
  }

  // ============================================================================
  // Utility Methods (inherited from BaseClient: healthCheck, updateConfig, getConfig, buildQueryString)
  // ============================================================================
}
